// src/components/Calculator/SingleResult.tsx
import { KPIResult, TargetType } from "@/lib/calculator";
import { formatCurrency, formatDate } from "@/lib/utils";
import { motion } from '@/lib/motion';

interface SingleResultProps {
  result: KPIResult;
  target: TargetType;
  annualIncome?: number; // Added prop for annual income
}

export function SingleResult({ result, target, annualIncome }: SingleResultProps) {
  if (!result) return null;

  const getMainValue = (): string => {
    switch (target) {
      case 'payment':
        return formatCurrency(result.monthlyPayment) + '/mo';
      case 'price':
        return formatCurrency(result.maxPrice);
      case 'date':
        return result.purchaseDate ? formatDate(result.purchaseDate) : 'Not possible';
      case 'savings':
        return formatCurrency(result.requiredSavings) + '/mo';
      default:
        return formatCurrency(result.maxPrice);
    }
  };

  const getDescriptiveText = (): string => {
    switch (target) {
      case 'payment':
        return `For a ${formatCurrency(result.targetPrice)} home with ${result.downPaymentPercentage.toFixed(1)}% down`;
      case 'price':
        return `With a ${formatCurrency(annualIncome || 0)} income and ${result.downPaymentPercentage.toFixed(1)}% down`;
      case 'date':
        return result.purchaseDate
          ? `To purchase a ${formatCurrency(result.targetPrice)} home`
          : `It is not possible to purchase a ${formatCurrency(result.targetPrice)} home with current savings rate`;
      case 'savings':
        return result.requiredSavings === Infinity
          ? `Cannot calculate required savings for immediate purchase of ${formatCurrency(result.targetPrice)} home`
          : `To buy a ${formatCurrency(result.targetPrice)} home by ${formatDate(result.purchaseDate || Date.now())}`;
      default:
        return '';
    }
  };

  const getHeadlineText = (): string => {
    switch (target) {
      case 'payment':
        return 'Your monthly payment is';
      case 'price':
        return 'Your maximum home price is';
      case 'date':
        return result.purchaseDate ? 'Your purchase date is' : 'Purchase not possible';
      case 'savings':
        return result.requiredSavings === Infinity ? 'Savings calculation not possible' : 'Your required savings are';
      default:
        return '';
    }
  };

  // Add animation for better user experience - with padding for proper alignment
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="p-3 sm:p-4"
    >
      <p className="text-gray-700 mb-1">{getDescriptiveText()}</p>
      <h3 className="text-xl font-semibold text-gray-900 mb-3">
        {getHeadlineText()}
      </h3>
      <div className="text-4xl font-bold text-primary">
        {getMainValue()}
      </div>
    </motion.div>
  );
}